import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { textSizeClasses, getSafeTextSizeClasses } from '../utils/textSizeClasses';
import { headerTranslations } from '../locales/header';

/**
 * HeaderAccessibilityMenu Component
 * Provides an accessible dropdown menu for users to customize accessibility settings.
 */
type HeaderAccessibilityMenuProps = {
  className?: string;
  onMenuToggle?: ((isOpen: boolean) => void) | null;
  translations?: any;
};

const HeaderAccessibilityMenu: React.FC<HeaderAccessibilityMenuProps> = ({
  className = '',
  onMenuToggle = null,
  translations = null,
}) => {
  const { state, actions } = useAppContext();
  const {
    highContrast,
    darkMode,
    soundEnabled,
    vibrationEnabled,
    language,
    textSize,
    reduceMotion,
  } = state;
  const {
    setTextSize,
    toggleReduceMotion,
    toggleHighContrast,
    toggleDarkMode,
    toggleSound,
    toggleVibration,
    setLanguage,
  } = actions;

  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  // Refs for focus management
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLElement[]>([]);

  // Use custom translations if provided, otherwise use context language
  const t =
    translations || headerTranslations[language as keyof typeof headerTranslations] || headerTranslations.es;

  const handleTextSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setTextSize(e.target.value);
    },
    [setTextSize]
  );

  const handleLanguageChange = useCallback(
    (lang: string) => {
      setLanguage(lang);
    },
    [setLanguage]
  );

  // Menu items for keyboard navigation (excluding text size and language which have special handling)
  const menuItems = [
    {
      id: 'reduceMotion',
      label: t.reduceMotion,
      handler: toggleReduceMotion,
      checked: reduceMotion,
    },
    {
      id: 'highContrast',
      label: t.highContrast,
      handler: toggleHighContrast,
      checked: highContrast,
    },
    {
      id: 'darkMode',
      label: darkMode ? t.darkModeOn : t.darkModeOff,
      handler: toggleDarkMode,
      checked: darkMode,
    },
    {
      id: 'soundEnabled',
      label: t.sound,
      handler: toggleSound,
      checked: soundEnabled,
    },
    {
      id: 'vibrationEnabled',
      label: t.vibration,
      handler: toggleVibration,
      checked: vibrationEnabled,
    },
  ];

  /**
   * Handles menu toggle with proper focus management
   */
  const handleMenuToggle = useCallback(() => {
    const newShowMenu = !showMenu;
    setShowMenu(newShowMenu);
    setFocusedIndex(-1);

    // Notify parent component if callback provided
    if (onMenuToggle) {
      onMenuToggle(newShowMenu);
    }

    // Focus management
    if (newShowMenu) {
      // Focus first focusable element when opening
      setTimeout(() => {
        const firstFocusable = menuRef.current?.querySelector(
          '[role="menuitem"], [role="menuitemcheckbox"], select, button'
        );
        if (firstFocusable) {
          (firstFocusable as HTMLElement).focus();
          setFocusedIndex(0);
        }
      }, 0);
    } else {
      // Return focus to menu button when closing
      if (menuButtonRef.current) {
        menuButtonRef.current.focus();
      }
    }
  }, [showMenu, onMenuToggle]);

  /**
   * Handles keyboard navigation within the menu
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!showMenu) return;

      const { key } = event;
      const totalItems = menuRef.current
        ? menuRef.current.querySelectorAll(
            '[role="menuitem"], [role="menuitemcheckbox"], select, button'
          ).length
        : 0;

      switch (key) {
        case 'Escape':
          event.preventDefault();
          setShowMenu(false);
          if (menuButtonRef.current) {
            menuButtonRef.current.focus();
          }
          break;

        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev < totalItems - 1 ? prev + 1 : 0;
            const nextElement = menuRef.current?.querySelectorAll(
              '[role="menuitem"], [role="menuitemcheckbox"], select, button'
            )[nextIndex];
            if (nextElement) {
              (nextElement as HTMLElement).focus();
            }
            return nextIndex;
          });
          break;

        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : totalItems - 1;
            const nextElement = menuRef.current?.querySelectorAll(
              '[role="menuitem"], [role="menuitemcheckbox"], select, button'
            )[nextIndex];
            if (nextElement) {
              (nextElement as HTMLElement).focus();
            }
            return nextIndex;
          });
          break;

        case 'Enter':
        case ' ':
          // Let the focused element handle the event naturally
          break;

        default:
          break;
      }
    },
    [showMenu]
  );

  /**
   * Close menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMenu, handleKeyDown]);

  return (
    <nav className={`relative ${className}`} aria-label={t.accessibility || undefined}>
      <button
        ref={menuButtonRef}
        onClick={handleMenuToggle}
        aria-haspopup='true'
        aria-expanded={showMenu}
        aria-label={`${t.accessibility || ''} ${showMenu ? 'menu open' : 'menu closed'}`}
        className={`px-6 py-3 ${getSafeTextSizeClasses(textSize as keyof typeof textSizeClasses).button} rounded-lg transition-colors focus:outline-none focus:ring-4 focus:ring-orange-500 focus:ring-opacity-50 ${
          highContrast
            ? 'bg-high-accent text-high-background hover:opacity-80'
            : 'bg-gray-200 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'
        }`}
      >
        {t.accessibility}
      </button>
      {showMenu && (
        <div
          ref={menuRef}
          role='menu'
          aria-label={t.accessibility || undefined}
          className={`absolute left-0 right-auto sm:right-0 sm:left-auto mt-2 w-64 max-h-[calc(100vh-2rem)] overflow-y-auto rounded-md shadow-lg py-1 z-20 ${
            highContrast
              ? 'bg-high-background text-high-foreground border-2 border-high-foreground'
              : 'bg-white dark:bg-gray-800 dark:text-gray-100 dark:border dark:border-gray-600 border border-gray-200'
          }`}
        >
          {/* Text Size Selection */}
          <div className='px-4 py-2' role='none'>
            <label
              htmlFor='textSize'
              className={`block ${getSafeTextSizeClasses(textSize).label} ${
                highContrast
                  ? 'text-high-foreground'
                  : 'text-gray-700 dark:text-gray-300'
              } mb-1 font-medium`}
            >
              {t.textSize}
            </label>
            <select
              id='textSize'
              value={textSize}
              onChange={handleTextSizeChange}
              role='menuitem'
              className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                highContrast
                  ? 'border-high-foreground bg-high-background text-high-foreground'
                  : 'border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100'
              } ${getSafeTextSizeClasses(textSize).input}`}
            >
              <option value='small'>{t.small}</option>
              <option value='medium'>{t.medium}</option>
              <option value='large'>{t.large}</option>
            </select>
          </div>

          {/* Checkbox Menu Items */}
          <ul role='none' className='list-none'>
            {menuItems.map((item: any, index: number) => (
              <li key={item.id} role='none'>
                <div
                  className='px-4 py-2 flex items-center hover:bg-gray-50 dark:hover:bg-gray-700'
                  role='menuitemcheckbox'
                  aria-checked={item.checked}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      item.handler();
                    }
                  }}
                  onClick={item.handler}
                  ref={(el) => {
                    if (el) menuItemsRef.current[index] = el;
                  }}
                >
                  <input
                    type="checkbox"
                    checked={item.checked}
                    readOnly
                    className="mr-2"
                    tabIndex={-1}
                  />
                  <label className="cursor-pointer select-none">
                    {item.label}
                  </label>
                </div>
              </li>
            ))}
          </ul>

          {/* Language Switcher */}
          <div
            className='px-4 py-2 border-t border-gray-200 dark:border-gray-600'
            role='none'
          >
            <label
              className={`block ${getSafeTextSizeClasses(textSize).label} ${
                highContrast ? 'text-high-foreground' : 'text-gray-700 dark:text-gray-300'
              } mb-1 font-medium`}
            >
              {t.language}
            </label>
            <div className='flex gap-2' role='group' aria-label={t.language || undefined}>
              <button
                onClick={() => handleLanguageChange('es')}
                role='menuitemradio'
                aria-checked={language === 'es'}
                className={`flex-1 px-3 py-2 rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  language === 'es'
                    ? highContrast
                      ? 'bg-high-accent text-high-background'
                      : 'bg-orange-300 dark:bg-orange-800 text-orange-900 dark:text-orange-100'
                    : highContrast
                      ? 'bg-high-background text-high-foreground border border-high-foreground hover:opacity-80'
                      : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                } ${getSafeTextSizeClasses(textSize).button}`}
              >
                ES
              </button>
              <button
                onClick={() => handleLanguageChange('en')}
                role='menuitemradio'
                aria-checked={language === 'en'}
                className={`flex-1 px-3 py-2 rounded-lg font-bold focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  language === 'en'
                    ? highContrast
                      ? 'bg-high-accent text-high-background'
                      : 'bg-orange-300 dark:bg-orange-800 text-orange-900 dark:text-orange-100'
                    : highContrast
                      ? 'bg-high-background text-high-foreground border border-high-foreground hover:opacity-80'
                      : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                } ${getSafeTextSizeClasses(textSize).button}`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default HeaderAccessibilityMenu;
